import { ChangeDetectorRef, Component, Input, ViewChild } from '@angular/core';
import { CiaoModalComponent } from '~app/components/shared/ciao-modal/ciao-modal.component';
import { CiaoSharedModule } from '~app/components/shared/shared.module';
import { CiaoFormsModule } from '../../../../shared/forms/forms.module';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TripService } from '~app/services';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { OkayAttributes, PaginationResult, TripAttributes } from '~app/models';
import { CheckpointAttributes } from '~app/models/checkpoint';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { DatetimeService } from '~app/services/datetime.service';
import { ToastrService } from 'ngx-toastr';
import { RolePermissionService } from '~app/services/role-permission.service';

/** Matches the backend's auto-satisfy window in Okay.resolveCheckpointsInWindow. */
const CHECKPOINT_WINDOW_MS = 3 * 60 * 60 * 1000;

type CheckpointStatusType =
  | 'submitted'
  | 'submittedLate'
  | 'late'
  | 'ready'
  | 'upcoming';
@Component({
  selector: 'ciao-scheduled-checkpoint-modal',
  imports: [
    CiaoSharedModule,
    CiaoFormsModule,
    NgIf,
    NgFor,
    NgClass,
    NgTemplateOutlet,
  ],
  templateUrl: './scheduled-checkpoint-modal.component.html',
  styleUrl: './scheduled-checkpoint-modal.component.less',
})
export class ScheduledCheckpointModalComponent {
  @Input() tripId?: string;
  @Input() requiresTripSelection?: boolean;
  @ViewChild('checkpointModal') checkpointModal: CiaoModalComponent;
  private subscriptions: Subscription = new Subscription();

  trip: TripAttributes;
  /** Current date/time when modal was opened; displayed read-only and sent on submit */
  reportedDateTime: Date = null;


  //for use in landing page implementation
  openAndLateTrips$: Observable<{ label: string; value: TripAttributes }[]>;

  formGroup = new FormGroup({
    tripId: new FormControl(null),
    reportedDateTime: new FormControl(null),
    notes: new FormControl(null),
  });

  sendingRequest = false;
  /** Drives the success check-mark animation shown briefly before the modal closes. */
  showSuccess = false;
  private successTimeout: ReturnType<typeof setTimeout>;

  constructor(
    private readonly tripService: TripService,
    private readonly dateTimeService: DatetimeService,
    private readonly toastrService: ToastrService,
    private readonly cdr: ChangeDetectorRef,
    private readonly rolePermissionService: RolePermissionService
  ) {}

  ngOnChanges() {}

  ngOnInit() {
    if (this.tripId) {
      this.subscriptions.add(
        this.tripService
          .findById(this.tripId)
          .pipe(
            tap((trip: TripAttributes) => {
              this.trip = trip;
            })
          )
          .subscribe()
      );
    } else {
      this.refreshTrips();
      this.formGroup.controls.tripId.addValidators([Validators.required]);
      this.subscriptions.add(
        this.formGroup.controls.tripId.valueChanges
          .pipe(
            filter((trip) => !!trip),
            distinctUntilChanged(),
            switchMap((trip: Partial<TripAttributes>) =>
              this.tripService.findById(trip.id)
            ),
            tap((trip: TripAttributes) => {
              this.trip = trip;
              this.tripId = trip.id;
              this.cdr.markForCheck();
            })
          )
          .subscribe()
      );
    }
  }

  /** Read-only display of current date/time used for this submission */
  currentDateTimeDisplay: string = '';

  private updateDateTimeDisplay(): void {
    const d = this.reportedDateTime ?? new Date();
    const iso = d instanceof Date ? d.toISOString() : String(d);
    const result = this.dateTimeService.formatDateTimeReadable(iso);
    this.currentDateTimeDisplay = typeof result === 'string' ? result : '';
  }

  get shouldDisableSubmit() {
    return (
      this.sendingRequest ||
      this.formGroup.invalid ||
      !this.trip ||
      this.trip?.tripStatus === 'Planned' ||
      this.trip?.tripStatus === 'Closed' ||
      this.trip?.tripStatus === 'Uncertain'
    );
  }

  /** All scheduled checkpoints for the selected trip, sorted by expected time (for modal list). */
  get sortedCheckpointsForModal(): CheckpointAttributes[] {
    const list = this.trip?.checkpoints ?? [];
    // Only show unsatisfied check-ins. Anything with an okayId is already
    // answered (Submitted or Missed) and needs no action in this modal.
    return list
      .filter((cp) => !cp.okayId)
      .sort(
        (a, b) =>
          new Date(a.expectedTime).valueOf() -
          new Date(b.expectedTime).valueOf()
      );
  }

  /** Unsatisfied check-ins this submission will satisfy (past-due or in-window). */
  get inWindowCheckpoints(): CheckpointAttributes[] {
    return this.sortedCheckpointsForModal.filter((cp) => {
      const type = this.getCheckpointStatusType(cp);
      return type === 'late' || type === 'ready';
    });
  }

  /** Future check-ins outside the window — this submission won't touch them. */
  get upcomingCheckpoints(): CheckpointAttributes[] {
    return this.sortedCheckpointsForModal.filter(
      (cp) => this.getCheckpointStatusType(cp) === 'upcoming'
    );
  }

  /**
   * Status type for styling:
   * - submitted (gray, disabled): already satisfied on time by an earlier okay.
   * - submittedLate (disabled, amber label): answered, but after its due time.
   * - late (red): overdue and still unsatisfied.
   * - ready (green): inside the 3-hour window, so this submit will satisfy it.
   * - upcoming (neutral): too far out for this submit to touch.
   */
  getCheckpointStatusType(cp: CheckpointAttributes): CheckpointStatusType {
    if (cp.status === 'Submitted') return 'submitted';
    if (cp.status === 'Missed') return 'submittedLate';
    if (cp.status === 'Late') return 'late';
    return this.isWithinOkayWindow(cp) ? 'ready' : 'upcoming';
  }

  /** Human-readable status label — used as the status icon's accessible text. */
  getCheckpointStatusLabel(cp: CheckpointAttributes): string {
    const type = this.getCheckpointStatusType(cp);
    if (type === 'submitted') return 'Submitted';
    if (type === 'submittedLate') return 'Missed';
    if (type === 'late') return cp.status;
    if (type === 'ready') return 'Ready';
    return 'Upcoming';
  }

  /**
   * Far-left status icon, using the same asset set as the Scheduled I'm Okay
   * Alerts table so the two stay visually consistent.
   */
  getCheckpointStatusIcon(cp: CheckpointAttributes): string {
    const base = 'assets/img/trip-2.0/checkpoint-status-icons/';
    switch (this.getCheckpointStatusType(cp)) {
      case 'submitted':
        return `${base}task_alt.svg`;
      case 'submittedLate':
        return `${base}block.svg`;
      case 'late':
        return `${base}emergency_home.svg`;
      default: // ready / upcoming
        return `${base}alarm.svg`;
    }
  }

  /**
   * True when the check-in falls in [timeOkay - 3hr, timeOkay + 3hr] — the same
   * window the backend uses to decide what this submission satisfies. Compared
   * against reportedDateTime (the timestamp actually sent) so the list can't
   * disagree with the result.
   */
  private isWithinOkayWindow(cp: CheckpointAttributes): boolean {
    if (!cp.expectedTime) return false;
    const expected = new Date(cp.expectedTime).valueOf();
    if (Number.isNaN(expected)) return false;
    const timeOkay = (this.reportedDateTime ?? new Date()).valueOf();
    return Math.abs(expected - timeOkay) <= CHECKPOINT_WINDOW_MS;
  }

  formatCheckpointTime(expectedTime: string): string {
    if (!expectedTime) return '';
    const result = this.dateTimeService.formatDateTimeReadable(expectedTime);
    return typeof result === 'string' ? result : '';
  }

  /** Date portion only, so dates can be aligned in their own column. */
  formatCheckpointDate(expectedTime: string): string {
    return this.formatCheckpointTime(expectedTime).split(/\s{2,}/)[0] ?? '';
  }

  /** Time portion only, right-aligned in a fixed-width column. */
  formatCheckpointTimeOnly(expectedTime: string): string {
    return this.formatCheckpointTime(expectedTime).split(/\s{2,}/)[1] ?? '';
  }

  /** Open modal with current date/time. Backend will auto-satisfy check-ins within 3-hour window or log unscheduled. */
  openCheckpointModal(_rowNode?: unknown) {
    const now = new Date();
    this.reportedDateTime = now;
    this.updateDateTimeDisplay();
    this.formGroup.patchValue({
      reportedDateTime: now.toISOString(),
      notes: this.formGroup.value.notes ?? '',
    });
    const modal = this.checkpointModal?.openSmallModal();
    modal?.result?.finally(() => {
      this.formGroup.reset();
      this.reportedDateTime = null;
      this.currentDateTimeDisplay = '';
      this.sendingRequest = false;
    });
  }

  /** Submit I'm Okay for current date/time. Backend resolves checkpoints in 3-hour window or logs unscheduled. */
  sendOkay() {
    if (!this.trip) {
      this.toastrService.error(
        `Trip was not updated successfully. Error: Trip Not Selected`,
        'Error'
      );
      console.error(
        'Something is very wrong.  Trip Not Selected.  Form Value:',
        this.formGroup.value
      );
      throw new Error('Trip Not Selected');
    }
    const timeOkay =
      this.reportedDateTime ?? new Date(this.formGroup.value.reportedDateTime);
    const okay: OkayAttributes = {
      tripId: this.tripId,
      type: 'okay',
      timeOkay: timeOkay instanceof Date ? timeOkay : new Date(timeOkay),
      notes: this.formGroup.value.notes ?? '',
      crewMembersSelected: this.trip.crewMembersProcessed,
      checkpoints: [],
    };
    this.sendingRequest = true;
    const okaySaveConfirmation = this.tripService.addOkay(okay);
    this.subscriptions.add(
      okaySaveConfirmation
        .pipe(
          tap(() => {
            this.sendingRequest = false;
            // Play the success check-mark animation, then close and confirm.
            this.showSuccess = true;
            this.successTimeout = setTimeout(() => {
              this.showSuccess = false;
              this.checkpointModal.dismiss('completed');
              this.toastrService.success(
                `I'm Okay submitted successfully.`,
                'Success'
              );
            }, 1600);
          }),
          catchError((err) => {
            this.checkpointModal.dismiss('error');
            this.sendingRequest = false;
            this.toastrService.error(
              `Trip was not updated successfully. Error: ${err}`,
              'Error'
            );
            return of(null);
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    clearTimeout(this.successTimeout);
  }

  closeEditModal() {
    this.formGroup.reset();
    this.checkpointModal.close('back to view');
  }

  // FOR USE FROM THE LANDING PAGE IMPLEMENTATION
  op_paginationResultsToOptions(
    input: Observable<PaginationResult<TripAttributes>>
  ) {
    return input.pipe(
      map((results) => results.rows),
      map((trips) =>
        trips.map((trip) => ({
          label: `${
            trip.crewMembers[0]?.crewMember?.displayName
          } | ${trip.startDate.substring(0, 10)} | ${trip.usergroup?.label}`,
          value: trip,
        }))
      ),
      shareReplay(1)
    );
  }

  searchForTripsByStatus(status: string) {
    // Dispatchers manage trips for their team but are never named on them, so
    // the imNamedOnThisTrip filter would always exclude every trip. They have
    // view_trips on the team, so the controller already team-scopes the result.
    return this.rolePermissionService.isDispatcher$.pipe(
      take(1),
      switchMap((isDispatcher) => {
        const where: [string, string?][] = isDispatcher
          ? [['tripStatus', status]]
          : [['tripStatus', status], ['imNamedOnThisTrip']];
        return this.tripService.search({
          limit: 25,
          offset: 0,
          order: [['startDate', 'ASC']],
          where,
        });
      })
    );
  }

  refreshTrips() {
    let openTrips$ = this.searchForTripsByStatus('Open').pipe(
      this.op_paginationResultsToOptions
    );
    let lateTrips$ = this.searchForTripsByStatus('Late').pipe(
      this.op_paginationResultsToOptions
    );

    this.openAndLateTrips$ = combineLatest([lateTrips$, openTrips$]).pipe(
      map(([lateTrips, openTrips]) => [...lateTrips, ...openTrips])
    );
  }
}
